export const MIN_AMOUNT_FOR_SAVINGS_LABEL = 5

export const PASSENGER_AGE = {
  START: 0,
  PRIMARY_MIN: 18,
  MINOR_CUTOFF: 11,
} as const

export const BASE_CURRENCIES = {
  USD: 'USD',
  CAD: 'CAD',
} as const
export type BaseCurrencies = (typeof BASE_CURRENCIES)[keyof typeof BASE_CURRENCIES]

export const CURRENCIES = {
  USD: BASE_CURRENCIES.USD,
  CAD: BASE_CURRENCIES.CAD,
  EUR: 'EUR',
  GBP: 'GBP',
  MXN: 'MXN',
  AUD: 'AUD',
  JPY: 'JPY',
  BRL: 'BRL',
  INR: 'INR',
  KRW: 'KRW',
  AED: 'AED',
  SGD: 'SGD',
} as const
export type Currencies = (typeof CURRENCIES)[keyof typeof CURRENCIES]

export const CURRENCY_COOKIE = 'currency'

export const PACKAGE_TYPES = {
  EVENT: 'EVENT',
  TRAVEL: 'TRAVEL',
}
export type PackageTypes = (typeof PACKAGE_TYPES)[keyof typeof PACKAGE_TYPES]

export const TICKET_TYPES = {
  EVENT: 'EVENT',
  PARKING: 'PARKING',
} as const

export type TicketTypes = (typeof TICKET_TYPES)[keyof typeof TICKET_TYPES]

export const TICKET_FORMATS = {
  MOBILE_TICKET: 'MOBILE_TICKET',
  PHYSICAL_TICKET: 'PHYSICAL_TICKET',
  E_TICKET: 'E_TICKET',
  FLASH: 'FLASH',
  PAPER_LESS: 'PAPER_LESS',
  GUEST: 'GUEST',
} as const

export type TicketFormats = (typeof TICKET_FORMATS)[keyof typeof TICKET_FORMATS]

export const PROVIDERS = {
  TICKET_EVOLUTION: 'TICKET_EVOLUTION',
  ZENTRUM_HUB: 'ZENTRUM_HUB',
} as const

export type Providers = (typeof PROVIDERS)[keyof typeof PROVIDERS]

export const REVIEW_CATEGORY = {
  EXCEPTIONAL: 'EXCEPTIONAL',
  WONDERFUL: 'WONDERFUL',
  VERY_GOOD: 'VERY_GOOD',
  GOOD: 'GOOD',
  NOTHING: 'NOTHING',
} as const

export type ReviewCategory = (typeof REVIEW_CATEGORY)[keyof typeof REVIEW_CATEGORY]

export const HOTEL_FACILITIES = {
  REFUNDABLE: 'REFUNDABLE',
  FREE_BREAKFAST: 'FREE_BREAKFAST',
  WIFI: 'WIFI',
  PARKING: 'PARKING',
  POOL: 'POOL',
} as const
export type HotelFacilities = (typeof HOTEL_FACILITIES)[keyof typeof HOTEL_FACILITIES]

export const HOTEL_AMENITIES = {
  ACCESSIBLE: 'ACCESSIBLE',
  AIRPORT_SHUTTLE: 'AIRPORT_SHUTTLE',
  ASSISTIVE_DEVICES: 'ASSISTIVE_DEVICES',
  ATM: 'ATM',
  BALL_ROOM: 'BALL_ROOM',
  BANQUET: 'BANQUET',
  BREAKFAST: 'BREAKFAST',
  BUSINESS_CENTER: 'BUSINESS_CENTER',
  FRONT_DESK: 'FRONT_DESK',
  FITNESS_ROOM: 'FITNESS_ROOM',
  GYM: 'GYM',
  INTERNET: 'INTERNET',
  JACUZZI: 'JACUZZI',
  KIDS_ACTIVITY: 'KIDS_ACTIVITY',
  KITCHEN: 'KITCHEN',
  OTHER: 'OTHER',
  PARKING: 'PARKING',
  POOL: 'POOL',
  RESTAURANT: 'RESTAURANT',
  SPA: 'SPA',
} as const
export type HotelAmenities = (typeof HOTEL_AMENITIES)[keyof typeof HOTEL_AMENITIES]

export const HOTEL_STICKER_TYPE = {
  OUR_PICK: 'ourPick',
  PREMIUM: 'premium',
  BEST_VALUE: 'bestValue',
  TOP_LOCATION: 'topLocation',
} as const

export type HotelSticker = (typeof HOTEL_STICKER_TYPE)[keyof typeof HOTEL_STICKER_TYPE]

/** ---------------------------- FLIGHTS ---------------------------- */

export const FLIGHT_UPGRADE_FACILITY_TYPE = {
  BONUS_MILES: 'BONUS_MILES',
  CARRY_ON_BAGGAGE: 'CARRY_ON_BAGGAGE',
  CHANGEABLE_TICKET: 'CHANGEABLE_TICKET',
  CHANGE_ANYTIME: 'CHANGE_ANYTIME',
  EXTRA_LEGROOM: 'EXTRA_LEGROOM',
  FREE_BAGGAGE: 'FREE_BAGGAGE',
  MEAL: 'MEAL',
  NO_SHOW: 'NO_SHOW',
  PRE_RESERVED_SEAT_ASSIGNMENT: 'PRE_RESERVED_SEAT_ASSIGNMENT',
  PRE_RESERVED_ADVANCE_SEAT_SELECTION: 'PRE_RESERVED_ADVANCE_SEAT_SELECTION',
  PREMIUM_SEAT: 'PREMIUM_SEAT',
  PRIORITY_BOARDING: 'PRIORITY_BOARDING',
  PRIORITY_CHECK_IN: 'PRIORITY_CHECK_IN',
  REFUNDABLE: 'REFUNDABLE',
  SNACKS: 'SNACKS',
} as const

export type FlightUpgradeFacilityType =
  (typeof FLIGHT_UPGRADE_FACILITY_TYPE)[keyof typeof FLIGHT_UPGRADE_FACILITY_TYPE]

export const FLIGHT_FACILITY_INCLUSION_TYPE = {
  INCLUDED: 'INCLUDED',
  NOT_INCLUDED: 'NOT_INCLUDED',
  CHARGEABLE: 'CHARGEABLE',
  OTHER: 'OTHER',
} as const

export type FlightFacilityInclusionType =
  (typeof FLIGHT_FACILITY_INCLUSION_TYPE)[keyof typeof FLIGHT_FACILITY_INCLUSION_TYPE]

// ------------------- BOOKING CUSTOMS ERROR ---------------
export const API_CUSTOM_ERROR_CODE = {
  BOOKSEATS_BOOKING_INVALID_SESSION: 'BS-B-IS',
  BOOKSEATS_BOOKING_PAYMENT_AUTHORIZATION: 'BS-B-PA',
  BOOKSEATS_BOOKING_PAYMENT_AUTHORIZATION_ACTION: 'BS-B-PAA',
  BOOKSEATS_BOOKING_PAYMENT_CAPTURE: 'BS-B-PC',
  BOOKSEATS_BOOKING_PAYMENT_METHOD: 'BS-B-PM',
  BOOKSEATS_BOOKING_PAYMENT_INTENT: 'BS-B-PI',
  BOOKSEATS_BOOKING_FRAUD_DETECTION_DECIDE: 'BS-B-FDDE',
  BOOKSEATS_BOOKING_FRAUD_DETECTION_CHECK_OUT_DENIED: 'BS-B-FDCKD',
  BOOKSEATS_BOOKING_FRAUD_DETECTION_DECISION: 'BS-B-FDDN',
  BOOKSEATS_BOOKING_FRAUD_DETECTION_CANCEL: 'BS-B-FDCL',
  BOOKSEATS_BOOKING_TICKET: 'BS-B-TI',
  BOOKSEATS_BOOKING_FLIGHT: 'BS-B-FL',
  BOOKSEATS_BOOKING_HOTEL: 'BS-B-HO',
  BOOKSEATS_BOOKING_INSURANCE: 'BS-B-IN',
} as const

// ------------------- BOOKING MODULES -------------------
export const MODULES = {
  TICKET: 'ticket',
  HOTEL: 'hotel',
  PAYMENT: 'payment',
  FLIGHT: 'flight',
  FRAUD_DETECTION: 'fraudDetection',
  BOOKING: 'booking',
  AFFIRM: 'affirm',
} as const

export type ModuleTypes = (typeof MODULES)[keyof typeof MODULES]

export const PACKAGE_EVENT_TYPES = {
  BOOKSEATS: 'BOOKSEATS',
} as const

export const isBookseatsEventType = (eventType?: string) =>
  eventType === PACKAGE_EVENT_TYPES.BOOKSEATS
